import React, { useState, useCallback, useEffect } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MapPin, Search, X, Loader2, Check, Navigation } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Map content component - required because useMap must be inside Map children
const MapContent = ({ selectedLocation, onLocationSelect, searchedLocation }) => {
  const map = useMap();
  
  // Pan to searched location
  useEffect(() => {
    if (map && searchedLocation) {
      map.panTo(searchedLocation);
      map.setZoom(17);
    }
  }, [map, searchedLocation]);
  
  return (
    <>
      {selectedLocation && (
        <AdvancedMarker 
          position={selectedLocation}
          title="Selected Location"
        >
          <div className="relative">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45"></div>
          </div>
        </AdvancedMarker>
      )}
    </>
  );
};

// Address search component
const AddressSearch = ({ onSearch, isSearching }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch(searchQuery);
    }
  };
  
  return (
    <form onSubmit={handleSearch} className="flex gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search address or place..."
          className="pl-10 pr-4"
        />
      </div>
      <Button type="submit" disabled={isSearching || !searchQuery.trim()}>
        {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
      </Button>
    </form>
  );
};

const GoogleMapsLocationPicker = ({ 
  value, 
  onChange, 
  defaultCenter = { lat: 12.9716, lng: 77.5946 }, // Bangalore default
  height = '350px',
  disabled = false,
  pincode = '',
  city = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(value?.lat && value?.lng ? value : null);
  const [searchedLocation, setSearchedLocation] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  
  // Update center based on pincode/city if provided
  useEffect(() => {
    if (pincode && city && !selectedLocation) {
      // Try to geocode the pincode
      geocodeAddress(`${pincode}, ${city}, India`);
    }
  }, [pincode, city]);
  
  // Geocode address using Google's Geocoding service
  const geocodeAddress = useCallback(async (address) => {
    if (!window.google?.maps?.Geocoder) {
      console.warn('Google Maps Geocoder not available');
      return;
    }
    
    setIsSearching(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });
      
      const location = {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng()
      };
      
      setMapCenter(location);
      setSearchedLocation(location);
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);
  
  // Handle map click
  const handleMapClick = useCallback((e) => {
    if (disabled) return;
    
    const latLng = e.detail?.latLng;
    if (latLng) {
      const location = { lat: latLng.lat, lng: latLng.lng };
      setSelectedLocation(location);
    }
  }, [disabled]);
  
  // Handle search
  const handleSearch = (query) => {
    geocodeAddress(query);
  };
  
  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    
    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setMapCenter(location);
        setSearchedLocation(location);
        setSelectedLocation(location);
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please search manually.');
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  
  // Confirm selection
  const handleConfirm = () => {
    if (selectedLocation) {
      onChange(selectedLocation);
      setIsOpen(false);
    }
  };
  
  // Cancel selection
  const handleCancel = () => {
    setSelectedLocation(value?.lat && value?.lng ? value : null);
    setIsOpen(false);
  };
  
  // Check if API key exists
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY') {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
        <MapPin className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">Google Maps API key not configured</p>
      </div>
    );
  }
  
  // Display mode (when map is not open)
  if (!isOpen) {
    return (
      <div className="space-y-2">
        <div 
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            value?.lat && value?.lng 
              ? 'border-green-300 bg-green-50 hover:bg-green-100' 
              : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={() => !disabled && setIsOpen(true)}
          data-testid="location-picker-trigger"
        >
          {value?.lat && value?.lng ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-green-700">Location Selected</p>
                <p className="text-xs text-green-600">
                  {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
                </p>
              </div>
              {!disabled && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(true);
                  }}
                >
                  Change
                </Button>
              )}
            </div>
          ) : (
            <>
              <MapPin className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm font-medium text-gray-600">Select Location on Map</p>
              <p className="text-xs text-muted-foreground mt-1">Click to open map and select location</p>
            </>
          )}
        </div>
      </div>
    );
  }
  
  // Map selection mode
  return (
    <div className="space-y-3">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        {/* Search bar */}
        <div className="flex gap-2">
          <div className="flex-1">
            <AddressSearch onSearch={handleSearch} isSearching={isSearching} />
          </div>
          <Button 
            variant="outline" 
            onClick={getCurrentLocation}
            disabled={isGettingLocation}
            title="Use my current location"
          >
            {isGettingLocation ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Navigation className="w-4 h-4" />
            )}
          </Button>
        </div>
        
        {/* Map */}
        <div className="rounded-lg overflow-hidden border" style={{ height }}>
          <Map
            style={{ width: '100%', height: '100%' }}
            defaultCenter={mapCenter}
            defaultZoom={14}
            mapId="franchise-location-picker"
            gestureHandling="greedy"
            disableDefaultUI={false}
            zoomControl={true}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            onClick={handleMapClick}
          >
            <MapContent 
              selectedLocation={selectedLocation}
              onLocationSelect={setSelectedLocation}
              searchedLocation={searchedLocation}
            />
          </Map>
        </div>
        
        {/* Instructions */}
        <p className="text-xs text-muted-foreground text-center">
          Click on the map to select the exact outlet location, or use search to find an address
        </p>
        
        {/* Selected coordinates */}
        {selectedLocation && (
          <div className="flex items-center justify-center gap-2 text-sm bg-green-50 border border-green-200 rounded-lg py-2 px-4">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-green-700">
              Selected: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
            </span>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!selectedLocation}
            data-testid="confirm-location-btn"
          >
            <Check className="w-4 h-4 mr-1" />
            Confirm Location
          </Button>
        </div>
      </APIProvider>
    </div>
  );
};

export default GoogleMapsLocationPicker;
